import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Award, BookOpen, Users, Star, Shield, Heart,
  Download, ExternalLink, Copy, ArrowLeft, Plus, CheckCircle, XCircle,
} from 'lucide-react';

// Production-safe API host: same-origin in deployed builds, env var in preview.
const _ENV_BACKEND = process.env.REACT_APP_BACKEND_URL || '';
const _ORIGIN = typeof window !== 'undefined' ? window.location.origin : '';
const _isPreviewEnv = /preview\.emergentagent\.com$/.test(
  (typeof window !== 'undefined' && window.location.hostname) || ''
);
const API = _isPreviewEnv ? _ENV_BACKEND : (_ORIGIN || _ENV_BACKEND);

const MembershipDashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [status, setStatus] = useState(null);
  const [ce, setCe] = useState({ total_hours: 0, entries: [] });
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ title: '', provider: '', hours: '', date: '' });
  const [copied, setCopied] = useState(false);
  const [openingPortal, setOpeningPortal] = useState(false);

  useEffect(() => {
    const raw = localStorage.getItem('user');
    if (!raw) {
      navigate('/login?return=/membership/dashboard');
      return;
    }
    const u = JSON.parse(raw);
    setUser(u);
    const uid = u.id || u._id;
    fetch(`${API}/api/membership/status/${uid}`).then((r) => r.json()).then(setStatus).catch(() => {});
    fetch(`${API}/api/membership/ce/${uid}`).then((r) => r.json()).then(setCe).catch(() => {});
  }, [navigate]);

  const reloadCe = useCallback(() => {
    if (!user) return;
    const uid = user.id || user._id;
    fetch(`${API}/api/membership/ce/${uid}`).then((r) => r.json()).then(setCe).catch(() => {});
  }, [user]);

  const logCe = async (e) => {
    e.preventDefault();
    if (!user) return;
    setAdding(true);
    try {
      const res = await fetch(`${API}/api/membership/ce/log`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: user.id || user._id,
          title: form.title,
          provider: form.provider,
          hours: parseFloat(form.hours) || 0,
          date: form.date,
        }),
      });
      if (!res.ok) {
        const t = await res.text();
        throw new Error(t);
      }
      setForm({ title: '', provider: '', hours: '', date: '' });
      reloadCe();
    } catch (err) {
      alert(`CE log error: ${err.message}`);
    } finally {
      setAdding(false);
    }
  };

  const openPortal = async () => {
    if (!user) return;
    setOpeningPortal(true);
    try {
      const res = await fetch(`${API}/api/membership/portal`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: user.id || user._id,
          origin_url: window.location.origin,
        }),
      });
      const data = await res.json();
      if (data.portal_url) {
        window.location.href = data.portal_url;
      } else {
        alert(data.detail || 'Could not open billing portal.');
      }
    } catch (e) {
      alert(`Portal error: ${e.message}`);
    } finally {
      setOpeningPortal(false);
    }
  };

  const copyDiscount = () => {
    if (status?.discount_code) {
      navigator.clipboard.writeText(status.discount_code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  };

  if (!user) return null;

  if (status && !status.is_member) {
    return (
      <div style={{ minHeight: '100vh', background: '#0f172a', color: '#e2e8f0', padding: '40px 24px' }}>
        <div style={{ maxWidth: 720, margin: '0 auto', textAlign: 'center', background: 'rgba(255,255,255,0.04)', padding: 40, borderRadius: 14 }}>
          <XCircle size={42} color="#f87171" />
          <h1 style={{ fontSize: '1.8rem', color: '#f8fafc', marginTop: 16 }}>You aren&apos;t a member yet</h1>
          <p style={{ color: '#cbd5e1', marginTop: 12 }}>Join the MedCare Association for $9.99 / month to access your member card, CE tracker, directory listing, and more.</p>
          <Link to="/association" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '12px 24px', borderRadius: 999, background: 'linear-gradient(90deg,#14b8a6,#a78bfa)', color: 'white', fontWeight: 700, textDecoration: 'none', marginTop: 22 }}>
            Become a Member
          </Link>
        </div>
      </div>
    );
  }

  const memberNumber = status?.member_number;
  const cardUrl = memberNumber ? `${API}/api/membership/card/${user.id || user._id}` : null;
  const badgeUrl = status?.badge_url ? `${API}/api${status.badge_url}` : null;

  return (
    <div style={{ minHeight: '100vh', background: '#0f172a', color: '#e2e8f0' }}>
      <div style={{ padding: '20px 40px', display: 'flex', alignItems: 'center', gap: 20 }}>
        <Link to="/" style={{ display: 'inline-flex', gap: 8, color: '#cbd5e1', textDecoration: 'none' }}>
          <ArrowLeft size={18} /> Home
        </Link>
        <Link to="/association" style={{ marginLeft: 'auto', color: '#5eead4', textDecoration: 'none' }}>About the Association</Link>
      </div>

      {/* Top hero card */}
      <section style={{ maxWidth: 1100, margin: '0 auto', padding: '20px 40px' }}>
        <div data-testid="member-dashboard-hero" style={{ background: 'linear-gradient(120deg, rgba(20,184,166,0.18), rgba(147,51,234,0.18))', borderRadius: 18, padding: 30, border: '1px solid rgba(255,255,255,0.08)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 20 }}>
            <div>
              <div style={{ fontSize: '0.78rem', letterSpacing: 1.2, color: '#fde68a', textTransform: 'uppercase' }}>Active Member</div>
              <h1 style={{ fontSize: '2rem', color: '#f8fafc', margin: '6px 0 0' }}>{user.name || user.full_name || user.email}</h1>
              <div style={{ color: '#cbd5e1', marginTop: 8, fontSize: '0.95rem' }}>
                Member # <strong data-testid="member-number" style={{ color: '#5eead4' }}>{memberNumber}</strong> · Status <strong style={{ color: '#34d399' }}>{status?.status?.toUpperCase()}</strong>
              </div>
              <div style={{ color: '#94a3b8', marginTop: 4, fontSize: '0.85rem' }}>
                Joined {(status?.joined_date || '').split('T')[0]} · Next renewal {(status?.current_period_end || '').split('T')[0]}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-start' }}>
              <a href={cardUrl} data-testid="download-member-card" style={{ display: 'inline-flex', gap: 8, alignItems: 'center', padding: '12px 18px', borderRadius: 999, background: '#0d9488', color: 'white', textDecoration: 'none', fontWeight: 600 }}>
                <Download size={16} /> Download Card
              </a>
              <button type="button" onClick={openPortal} disabled={openingPortal} data-testid="open-billing-portal" style={{ display: 'inline-flex', gap: 8, alignItems: 'center', padding: '12px 18px', borderRadius: 999, background: 'transparent', color: '#e2e8f0', border: '1px solid rgba(255,255,255,0.2)', cursor: 'pointer', fontWeight: 600 }}>
                <ExternalLink size={16} /> {openingPortal ? 'Opening…' : 'Manage Billing'}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits grid */}
      <section style={{ maxWidth: 1100, margin: '0 auto', padding: '24px 40px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(280px,1fr))', gap: 16 }}>
          {/* Digital card */}
          <div style={{ background: 'rgba(255,255,255,0.04)', borderRadius: 14, padding: 22, border: '1px solid rgba(255,255,255,0.06)' }}>
            <Award size={26} color="#fde68a" />
            <div style={{ fontWeight: 700, color: '#f8fafc', marginTop: 10 }}>Digital Membership Card</div>
            <p style={{ color: '#94a3b8', fontSize: '0.9rem', marginTop: 6, lineHeight: 1.55 }}>Wallet-sized PDF — print or save to phone. Includes your member number and division stamp.</p>
            <a href={cardUrl} style={{ display: 'inline-flex', gap: 6, alignItems: 'center', marginTop: 10, color: '#5eead4', textDecoration: 'none', fontWeight: 600 }}>
              <Download size={14} /> Download PDF
            </a>
          </div>

          {/* Member directory */}
          <div style={{ background: 'rgba(255,255,255,0.04)', borderRadius: 14, padding: 22, border: '1px solid rgba(255,255,255,0.06)' }}>
            <Users size={26} color="#5eead4" />
            <div style={{ fontWeight: 700, color: '#f8fafc', marginTop: 10 }}>Member Directory</div>
            <p style={{ color: '#94a3b8', fontSize: '0.9rem', marginTop: 6, lineHeight: 1.55 }}>Your name and member number are visible to employers and clients.</p>
            <Link to="/membership/directory" style={{ display: 'inline-flex', gap: 6, alignItems: 'center', marginTop: 10, color: '#5eead4', textDecoration: 'none', fontWeight: 600 }}>
              View Directory <ExternalLink size={14} />
            </Link>
          </div>

          {/* Discount code */}
          <div style={{ background: 'rgba(255,255,255,0.04)', borderRadius: 14, padding: 22, border: '1px solid rgba(255,255,255,0.06)' }}>
            <Star size={26} color="#fbbf24" />
            <div style={{ fontWeight: 700, color: '#f8fafc', marginTop: 10 }}>10% Member Discount</div>
            <p style={{ color: '#94a3b8', fontSize: '0.9rem', marginTop: 6, lineHeight: 1.55 }}>Use this code at checkout for any future MedCare course.</p>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10 }}>
              <code data-testid="member-discount-code" style={{ background: '#1e293b', padding: '8px 12px', borderRadius: 8, color: '#fde68a', fontWeight: 700, letterSpacing: 1.5 }}>{status?.discount_code}</code>
              <button type="button" onClick={copyDiscount} style={{ background: 'transparent', color: '#5eead4', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 6, padding: '6px 10px', cursor: 'pointer', display: 'inline-flex', gap: 4, alignItems: 'center', fontSize: '0.82rem' }}>
                {copied ? <><CheckCircle size={12} /> Copied</> : <><Copy size={12} /> Copy</>}
              </button>
            </div>
          </div>

          {/* Members-only library */}
          <div style={{ background: 'rgba(255,255,255,0.04)', borderRadius: 14, padding: 22, border: '1px solid rgba(255,255,255,0.06)' }}>
            <Shield size={26} color="#a78bfa" />
            <div style={{ fontWeight: 700, color: '#f8fafc', marginTop: 10 }}>Members-Only Library</div>
            <p style={{ color: '#94a3b8', fontSize: '0.9rem', marginTop: 6, lineHeight: 1.55 }}>Bonus NCLEX-PN banks, ethics templates, and study guides.</p>
            <Link to="/membership/library" style={{ display: 'inline-flex', gap: 6, alignItems: 'center', marginTop: 10, color: '#5eead4', textDecoration: 'none', fontWeight: 600 }}>
              Open Library <BookOpen size={14} />
            </Link>
          </div>

          {/* Badge */}
          <div style={{ background: 'rgba(255,255,255,0.04)', borderRadius: 14, padding: 22, border: '1px solid rgba(255,255,255,0.06)' }}>
            <Heart size={26} color="#f472b6" />
            <div style={{ fontWeight: 700, color: '#f8fafc', marginTop: 10 }}>Verifiable Digital Badge</div>
            <p style={{ color: '#94a3b8', fontSize: '0.9rem', marginTop: 6, lineHeight: 1.55 }}>Share this URL on LinkedIn — anyone can verify your active membership.</p>
            <a href={badgeUrl} target="_blank" rel="noreferrer" data-testid="member-badge-link" style={{ display: 'inline-flex', gap: 6, alignItems: 'center', marginTop: 10, color: '#5eead4', textDecoration: 'none', fontWeight: 600, wordBreak: 'break-all' }}>
              Verify Badge <ExternalLink size={14} />
            </a>
          </div>

          {/* CE tracker summary */}
          <div style={{ background: 'rgba(255,255,255,0.04)', borderRadius: 14, padding: 22, border: '1px solid rgba(255,255,255,0.06)' }}>
            <BookOpen size={26} color="#34d399" />
            <div style={{ fontWeight: 700, color: '#f8fafc', marginTop: 10 }}>CE Credits Logged</div>
            <div style={{ marginTop: 10, fontSize: '2rem', color: '#34d399', fontWeight: 800 }}>{ce.total_hours.toFixed(1)} hrs</div>
            <div style={{ color: '#94a3b8', fontSize: '0.85rem' }}>{ce.entries.length} entries on file</div>
          </div>
        </div>
      </section>

      {/* CE log form + table */}
      <section style={{ maxWidth: 1100, margin: '0 auto', padding: '24px 40px 60px' }}>
        <h2 style={{ fontSize: '1.25rem', color: '#f8fafc' }}>Continuing Education Log</h2>
        <form onSubmit={logCe} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10, marginTop: 14, background: 'rgba(255,255,255,0.04)', padding: 18, borderRadius: 12 }}>
          <input data-testid="ce-title" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Course title" style={{ background: '#0f172a', border: '1px solid rgba(255,255,255,0.1)', color: '#f8fafc', padding: '10px 12px', borderRadius: 8 }} />
          <input data-testid="ce-provider" required value={form.provider} onChange={(e) => setForm({ ...form, provider: e.target.value })} placeholder="Provider / school" style={{ background: '#0f172a', border: '1px solid rgba(255,255,255,0.1)', color: '#f8fafc', padding: '10px 12px', borderRadius: 8 }} />
          <input data-testid="ce-hours" required type="number" min="0.25" step="0.25" value={form.hours} onChange={(e) => setForm({ ...form, hours: e.target.value })} placeholder="Hours" style={{ background: '#0f172a', border: '1px solid rgba(255,255,255,0.1)', color: '#f8fafc', padding: '10px 12px', borderRadius: 8 }} />
          <input data-testid="ce-date" required type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} style={{ background: '#0f172a', border: '1px solid rgba(255,255,255,0.1)', color: '#f8fafc', padding: '10px 12px', borderRadius: 8 }} />
          <button data-testid="ce-add-btn" type="submit" disabled={adding} style={{ background: '#14b8a6', color: 'white', border: 0, borderRadius: 8, padding: '10px 16px', fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, justifyContent: 'center' }}>
            <Plus size={14} /> {adding ? 'Adding…' : 'Log CE Hours'}
          </button>
        </form>

        <div style={{ marginTop: 20, background: 'rgba(255,255,255,0.04)', borderRadius: 12, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', color: '#cbd5e1', fontSize: '0.9rem' }}>
            <thead>
              <tr style={{ background: 'rgba(255,255,255,0.06)', textAlign: 'left' }}>
                <th style={{ padding: 12 }}>Date</th>
                <th style={{ padding: 12 }}>Course</th>
                <th style={{ padding: 12 }}>Provider</th>
                <th style={{ padding: 12, textAlign: 'right' }}>Hours</th>
              </tr>
            </thead>
            <tbody>
              {ce.entries.length === 0 ? (
                <tr><td colSpan="4" style={{ padding: 20, textAlign: 'center', color: '#64748b' }}>No CE entries yet — add your first above.</td></tr>
              ) : ce.entries.map((e) => (
                <tr key={e.id} style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}>
                  <td style={{ padding: 12 }}>{e.date}</td>
                  <td style={{ padding: 12 }}>{e.title}</td>
                  <td style={{ padding: 12 }}>{e.provider}</td>
                  <td style={{ padding: 12, textAlign: 'right', color: '#34d399' }}>{Number(e.hours).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      <div style={{ padding: '24px 40px', textAlign: 'center', color: '#64748b', fontSize: '0.85rem' }}>
        MedCare Nursing Institute &amp; Training Academy · 3150 E. Shelby Dr, Memphis TN 38118 · 901.625.1201
      </div>
    </div>
  );
};

export default MembershipDashboard;
